import { useMemo, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageHero from '@/components/feature/PageHero';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { articles, articleCategories } from '@/mocks/industry';

export default function IndustryConsulting() {
  const [category, setCategory] = useState('全部');
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    return articles.filter((a) => {
      const matchCat = category === '全部' || a.category === category;
      const q = keyword.trim().toLowerCase();
      const matchKw =
        !q ||
        a.title.toLowerCase().includes(q) ||
        a.summary.toLowerCase().includes(q) ||
        a.keywords.some((k) => k.toLowerCase().includes(q));
      return matchCat && matchKw;
    });
  }, [category, keyword]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <SEO
        title="行业咨询 | 人工智能与乡村数字经济行业洞察"
        description="华辉数智行业洞察与专业内容中心，持续更新人工智能在县域应用、乡村数字经济、县域数字商业等方向的行业观点与研究。"
        keywords="行业咨询,人工智能应用,乡村数字经济,县域数字商业,行业洞察"
        path="/industry-consulting"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '行业咨询',
          description: '人工智能与乡村数字经济行业洞察',
        }}
      />
      <PageHero
        eyebrow="产品与解决方案 · 行业咨询"
        title="行业洞察与专业内容"
        titleHighlight="观点 · 研究 · 趋势"
        subtitle="持续更新的行业观点、研究与专业内容，构建华辉数智的行业洞察中心。"
        desc="与「相关资料」区分：相关资料承载企业与项目资料下载，行业咨询承载持续更新的行业观点与研究。"
        imageUrl="https://readdy.ai/api/search-image?query=Abstract%20knowledge%20and%20insight%20visualization%20with%20open%20books%2C%20light%20bulbs%20and%20flowing%20data%20streams%20connecting%20rural%20digital%20economy%20concepts%2C%20deep%20graphite%20background%20with%20green%20and%20gold%20accent%20glows%2C%20professional%20consulting%20aesthetic%2C%20clean%20minimal%20composition%2C%20cinematic%20lighting&width=1600&height=900&seq=industry-hero-01&orientation=landscape"
        imageAlt="行业洞察与专业内容"
      />
      <Breadcrumb items={[{ label: '产品与解决方案' }, { label: '行业咨询' }]} />

      <main>
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            {/* 搜索与筛选 */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
              <div className="flex flex-wrap gap-2">
                {articleCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setCategory(cat)}
                    className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      category === cat
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="relative w-full lg:w-72">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索文章或关键词"
                  className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
              </div>
            </div>

            {/* 文章列表 */}
            <div className="mt-8">
              {filtered.length === 0 ? (
                <div className="text-center py-16 text-foreground-500">
                  <i className="ri-inbox-line text-3xl"></i>
                  <p className="mt-3 text-sm">未找到相关文章，请调整筛选或搜索条件。</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {filtered.map((a) => (
                    <Reveal key={a.id}>
                      <article className="p-6 rounded-lg bg-background-100 border border-background-200 h-full flex flex-col">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-3 py-1 text-xs rounded-full bg-accent-100 text-accent-900 whitespace-nowrap">
                            {a.category}
                          </span>
                          <span className="text-xs text-foreground-500">{a.date}</span>
                        </div>
                        <h3 className="mt-3 font-heading font-bold text-foreground-950 text-lg leading-snug">
                          {a.title}
                        </h3>
                        <p className="mt-2 text-sm text-foreground-600 leading-relaxed flex-1">{a.summary}</p>
                        <div className="mt-4 flex items-center justify-between">
                          <div className="flex flex-wrap gap-1.5">
                            {a.keywords.map((k) => (
                              <span key={k} className="text-xs text-foreground-500 whitespace-nowrap">
                                #{k}
                              </span>
                            ))}
                          </div>
                          <span className="text-sm text-primary-600 whitespace-nowrap cursor-pointer">
                            阅读全文
                          </span>
                        </div>
                      </article>
                    </Reveal>
                  ))}
                </div>
              )}
            </div>

            {/* 推荐阅读 */}
            <Reveal className="mt-14">
              <SectionHeading
                eyebrow="推荐阅读"
                title="更多专业内容"
                subtitle="深入了解华辉数智在 AI 应用、乡村数字经济与县域数字商业领域的实践与思考。"
              />
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}